<template>
  <!-- S04 · Documentacion: listados con codigo autogenerado (documentacion y correspondencia),
       documentos (tipo seleccionable + PDF), comunicaciones y aceptacion de entregables. -->
  <div class="s04">
    <summary-card :title="$t('prm_summary_s04_doclist_title')" :mode="mode" :mark="true" chip="none">
      <template #extra>
        <button class="s04-xls" :disabled="!!exporting" @click="exportList('doc-list')">
          <b-icon pack="fas" :icon="exporting === 'doc-list' ? 'spinner' : 'file-excel'" :custom-class="exporting === 'doc-list' ? 'fa-spin' : ''" size="is-small"></b-icon>
          <span>{{ $t('prm_summary_export_button') }}</span>
        </button>
      </template>
      <prm-doc-list :rows="s04.docList || []" :issuers="s04.issuers || []" :sap="sap"
        :project-id="projectId" :base-url="baseUrl" :files="docListFiles" :mark="mode" :reset-key="resetKey"
        @save-list="(p) => $emit('save-list', p)"
        @files-changed="$emit('files-changed')" @remove-file="(id) => $emit('remove-file', id)"></prm-doc-list>
    </summary-card>

    <summary-card :title="$t('prm_summary_s04_corr_title')" :mode="mode" :mark="true" chip="none">
      <template #extra>
        <button class="s04-xls" :disabled="!!exporting" @click="exportList('correspondence')">
          <b-icon pack="fas" :icon="exporting === 'correspondence' ? 'spinner' : 'file-excel'" :custom-class="exporting === 'correspondence' ? 'fa-spin' : ''" size="is-small"></b-icon>
          <span>{{ $t('prm_summary_export_button') }}</span>
        </button>
      </template>
      <prm-correspondence-list :rows="s04.correspondence || []" :sap="sap" :owner-options="ownerOptions"
        :project-id="projectId" :base-url="baseUrl" :files="correspondenceFiles" :mark="mode" :reset-key="resetKey"
        @save-list="(p) => $emit('save-list', p)"
        @files-changed="$emit('files-changed')" @remove-file="(id) => $emit('remove-file', id)"></prm-correspondence-list>
    </summary-card>

    <summary-card :title="$t('prm_summary_s04_documents')" :subtitle="docSubtitle" :mode="mode" :mark="true" chip="none">
      <template #extra>
        <button class="tbl-add" @click="$refs.docTbl.addRow()"><b-icon pack="fas" icon="plus" size="is-small"></b-icon><span>{{ $t('prm_summary_s04_add_document') }}</span></button>
      </template>
      <prm-documents-table ref="docTbl" :rows="s04.documents || []" :doc-type-options="docTypeOptions" :yn-options="ynOptions"
        :project-id="projectId" :base-url="baseUrl" :files="documentFiles" :mark="mode" :reset-key="resetKey"
        @change="(items) => $emit('save-list', { path: 's04.documents', items })"
        @files-changed="$emit('files-changed')" @remove-file="(id) => $emit('remove-file', id)"></prm-documents-table>
    </summary-card>

    <summary-card :title="$t('prm_summary_s04_communications')" :mode="mode" :mark="true" chip="none">
      <prm-summary-editable-table :rows="s04.communications || []" :columns="communicationColumns" :blank="blankCommunication"
        :mark="mode" :add-label="$t('prm_summary_add_row')" :actions-label="$t('prm_summary_s03_actions')"
        :remove-title="$t('prm_summary_remove_row')" :empty-text="$t('prm_summary_empty')" :reset-key="resetKey"
        @change="(items) => $emit('save-list', { path: 's04.communications', items })"></prm-summary-editable-table>
    </summary-card>

    <summary-card :title="$t('prm_summary_s04_deliverables')" :mode="mode" :mark="true" chip="none">
      <prm-summary-editable-table :rows="s04.deliverables || []" :columns="deliverableColumns" :blank="blankDeliverable"
        :mark="mode" :add-label="$t('prm_summary_add_row')" :actions-label="$t('prm_summary_s03_actions')"
        :remove-title="$t('prm_summary_remove_row')" :empty-text="$t('prm_summary_empty')" :reset-key="resetKey"
        @change="(items) => $emit('save-list', { path: 's04.deliverables', items })"></prm-summary-editable-table>
    </summary-card>
  </div>
</template>

<script>
  import axios from 'axios'
  import SummaryCard from './summary-card.vue'
  import PrmSummaryEditableTable from './editable-table.vue'
  import PrmDocumentsTable from './documents-table.vue'
  import PrmDocList from './doc-list.vue'
  import PrmCorrespondenceList from './correspondence-list.vue'
  import { userOptions } from './summary-model'

  // Catálogo de tipos de documento (valor con código estable, etiqueta traducida).
  const DOC_TYPES = [
    'detailed_schedule', 'quality_plan', 'health_safety_plan', 'assembly_drawings',
    'operation_maintenance_manuals', 'fat_protocol', 'sat_protocol', 'monthly_report', 'master_document_list',
  ]

  export default {
    name: 'PrmSummaryS04',

    components: { SummaryCard, PrmSummaryEditableTable, PrmDocumentsTable, PrmDocList, PrmCorrespondenceList },

    props: {
      doc: { type: Object, required: true },
      sources: { type: Object, default: () => ({}) },
      project: { type: Object, default: () => ({}) },
      mode: { type: Boolean, default: false },
      resetKey: { type: Number, default: 0 },
    },

    data: () => ({ exporting: null }), // 'doc-list' | 'correspondence' mientras se genera el Excel

    computed: {
      s04 () { return this.doc.s04 || {} },
      projectId () { return this.project && this.project.id },
      baseUrl () { return `/api/v2/project/${this.projectId}/dashboard-prm` },
      documentFiles () { return (this.sources.contractFiles && this.sources.contractFiles.document) || {} },
      docListFiles () { return (this.sources.contractFiles && this.sources.contractFiles.doc_list) || {} },
      correspondenceFiles () { return (this.sources.contractFiles && this.sources.contractFiles.correspondence) || {} },
      docSubtitle () { return `${(this.s04.documents || []).length} ${this.$t('prm_summary_s04_docs_registered')}` },
      // SAP / código de proyecto: manual (S02) y si está vacío, la referencia real del proyecto.
      sap () { return String((this.doc.s01 && this.doc.s01.sap) || this.sources.sapCode || '') },

      // Responsable: usuarios del proyecto (fuente real, de sources).
      ownerOptions () { return userOptions(this.sources.users) },
      ynOptions () {
        return ['yes', 'no', 'na'].map((v) => ({ value: v, label: this.$t('prm_summary_' + v) }))
      },
      docTypeOptions () {
        return DOC_TYPES.map((v) => ({ value: v, label: this.$t('prm_summary_s04_doctype_' + v) }))
      },
      communicationColumns () {
        return [
          { key: 'type', label: this.$t('prm_summary_col_type'), type: 'text' },
          { key: 'procedure', label: this.$t('prm_summary_s04_col_procedure'), type: 'text', width: '30%' },
          { key: 'time', label: this.$t('prm_summary_s04_col_time'), type: 'text', align: 'center' },
          { key: 'channel', label: this.$t('prm_summary_s04_col_channel'), type: 'text', align: 'center' },
          { key: 'owner', label: this.$t('prm_summary_col_owner'), type: 'select', align: 'center', options: this.ownerOptions },
          { key: 'comments', label: this.$t('prm_summary_s04_col_comments'), type: 'text' },
        ]
      },
      deliverableColumns () {
        return [
          { key: 'area', label: this.$t('prm_summary_s04_col_area'), type: 'text' },
          { key: 'conditions', label: this.$t('prm_summary_s04_col_conditions'), type: 'text', width: '30%' },
          { key: 'evidence', label: this.$t('prm_summary_s04_col_evidence'), type: 'text', align: 'center' },
          { key: 'owner', label: this.$t('prm_summary_col_owner'), type: 'select', align: 'center', options: this.ownerOptions },
          { key: 'date', label: this.$t('prm_summary_s04_col_date'), type: 'date', align: 'center' },
          { key: 'comments', label: this.$t('prm_summary_s04_col_comments'), type: 'text' },
        ]
      },
      blankCommunication () { return { type: '', procedure: '', time: '', channel: '', owner: '', comments: '' } },
      blankDeliverable () { return { area: '', conditions: '', evidence: '', owner: '', date: '', comments: '' } },
    },

    methods: {
      // Descarga el Excel de un listado (doc-list | correspondence) desde el backend.
      async exportList (kind) {
        if (this.exporting) return
        this.exporting = kind
        try {
          const res = await axios.get(`${this.baseUrl}/summary/export/${kind}`, { responseType: 'blob' })
          const url = window.URL.createObjectURL(new Blob([res.data]))
          const a = document.createElement('a')
          a.href = url
          const title = kind === 'correspondence' ? this.$t('prm_summary_s04_corr_title') : this.$t('prm_summary_s04_doclist_title')
          a.download = `${title} - ${this.project.name || 'export'}.xlsx`
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)
          window.URL.revokeObjectURL(url)
        } catch (e) {
          this.$notify.error(this.$t('error_retrieving_data'))
        } finally {
          this.exporting = null
        }
      },
    },
  }
</script>

<style lang="scss" scoped>
  .s04 { display: flex; flex-direction: column; gap: 10px; }

  // Botón "Añadir documento" en la cabecera (arriba a la derecha, como el diseño).
  .tbl-add { display: inline-flex; align-items: center; gap: 5px; border: 1px solid #9fb5cc; background: #eef5fc; color: #2f5f8f; font-size: 10px; font-weight: 600; padding: 4px 10px; border-radius: 4px; cursor: pointer; white-space: nowrap; }
  .tbl-add:hover { background: #e3eefa; }

  // Botón "Generar Excel" de cada listado (verde, como el diseño).
  .s04-xls { display: inline-flex; align-items: center; gap: 5px; border: 0; background: #21824b; color: #fff; font-size: 10px; font-weight: 600; padding: 5px 10px; border-radius: 4px; cursor: pointer; white-space: nowrap; }
  .s04-xls:hover:not(:disabled) { background: #19663a; }
  .s04-xls:disabled { opacity: .6; cursor: not-allowed; }
</style>
